import { useMemo, useRef, useState, type ChangeEvent } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Sparkles,
  RefreshCw,
  Upload,
} from "lucide-react";

import { useAdminOrganizations } from "@/hooks/queries/useAdminOrganizations";
import { useCompanyDocuments } from "@/hooks/queries/useCompanyDocuments";
import {
  useExtractCompanyDocuments,
  useSetCompanyDocumentStatus,
  useUploadCompanyDocument,
} from "@/hooks/mutations/useCompanyDocumentMutations";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { CompanyDocument } from "@/types/workadvisory";

// Alineado con MAX_UPLOAD_BYTES del backend: el archivo viaja en base64.
const MAX_FILE_BYTES = 15 * 1024 * 1024;
// La lectura va por Gemini, que solo entiende PDF e imágenes.
const ACCEPTED = ".pdf,.jpg,.jpeg,.png";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Cuánto le queda de vida a un documento. Una certificación vencida deja de
 * sumar puntos en la evaluación, así que avisarlo con tiempo es el punto de esta
 * pantalla tanto como cargar archivos.
 */
function expiryState(document: CompanyDocument) {
  const hasta = document.organizationdocument_data?.vigencia?.hasta;
  if (!hasta) return null;

  const days = dayjs(hasta).diff(dayjs(), "day");
  if (Number.isNaN(days)) return null;
  if (days < 0)
    return { label: `Vencido el ${formatDate(hasta)}`, tone: "text-red-600" };
  if (days <= 90)
    return {
      label: `Vence en ${days} día${days === 1 ? "" : "s"}`,
      tone: "text-amber-600",
    };
  return { label: `Vigente hasta ${formatDate(hasta)}`, tone: "text-muted-foreground" };
}

function DocumentRow({ document }: { document: CompanyDocument }) {
  const setStatus = useSetCompanyDocumentStatus();
  const data = document.organizationdocument_data;
  const expiry = expiryState(document);
  const failed = document.organizationdocument_processingwasok === false;

  return (
    <div
      className={cn(
        "rounded-xl border bg-card p-4",
        !document.organizationdocument_status && "opacity-50",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {data?.tipo && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                {data.tipo}
              </span>
            )}
            <span className="text-sm font-bold">
              {data?.titulo ?? document.organizationdocument_name}
            </span>
            {!document.organizationdocument_beenprocessed && (
              <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-600">
                <Clock className="size-3" /> pendiente de leer
              </span>
            )}
            {failed && (
              <span className="flex items-center gap-1 text-[11px] font-semibold text-red-600">
                <AlertTriangle className="size-3" /> no se pudo leer
              </span>
            )}
          </div>

          <p className="mt-1 text-xs text-muted-foreground">
            {document.organizationdocument_name} ·{" "}
            {(document.organizationdocument_bytessize / 1048576).toFixed(1)} MB
            {data?.emisor && ` · ${data.emisor}`}
          </p>

          {expiry && (
            <p className={cn("mt-1 text-xs font-semibold", expiry.tone)}>
              {expiry.label}
            </p>
          )}

          {data?.resumen && (
            <p className="mt-2 max-w-3xl text-sm leading-snug">{data.resumen}</p>
          )}

          {data?.obras && data.obras.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold text-primary">
                {data.obras.length} obra(s) declarada(s)
              </summary>
              <ul className="mt-1.5 space-y-1">
                {data.obras.map((obra, index) => (
                  <li key={index} className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground/80">
                      {obra.monto ?? "—"}
                    </span>{" "}
                    · {obra.entidad ?? "—"} · {obra.descripcion}
                    {obra.ubicacion && ` · ${obra.ubicacion}`}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {data?.datosClave && data.datosClave.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs font-semibold text-muted-foreground">
                {data.datosClave.length} dato(s) clave
              </summary>
              <ul className="mt-1.5 space-y-0.5">
                {data.datosClave.map((dato, index) => (
                  <li key={index} className="text-xs text-muted-foreground">
                    <span className="font-semibold">{dato.campo}:</span>{" "}
                    {dato.valor}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <Button
          size="sm"
          variant="ghost"
          disabled={setStatus.isPending}
          onClick={() =>
            setStatus.mutate({
              documentId: document.organizationdocument_id,
              status: !document.organizationdocument_status,
            })
          }
        >
          {document.organizationdocument_status ? "Desactivar" : "Reactivar"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminCompanyDocuments() {
  const { data: organizations } = useAdminOrganizations();
  const [organizationId, setOrganizationId] = useState("");
  const { data: documents, isLoading } = useCompanyDocuments(organizationId);
  const upload = useUploadCompanyDocument();
  const extract = useExtractCompanyDocuments();
  const inputRef = useRef<HTMLInputElement>(null);

  const pending = useMemo(
    () =>
      documents?.filter((d) => !d.organizationdocument_beenprocessed).length ?? 0,
    [documents],
  );

  const onFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = "";
    if (!organizationId) return toast.error("Elige una empresa primero");

    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} pesa más de 15 MB`);
        continue;
      }
      try {
        await upload.mutateAsync({
          organizationId,
          fileName: file.name,
          base64Data: await fileToBase64(file),
        });
        toast.success(`${file.name} cargado`);
      } catch (err: any) {
        toast.error(err?.message ?? `No se pudo cargar ${file.name}`);
      }
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">
          Documentos de la empresa
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Lo que la constructora acredita: certificaciones, constancias,
          experiencia. Es la otra mitad del análisis — las bases dicen qué piden,
          esto dice qué tiene.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-card p-4">
        <label className="flex flex-col gap-1 text-xs font-semibold">
          Empresa
          <select
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
            className="min-w-56 rounded-md border bg-background px-2.5 py-1.5 text-sm font-normal"
          >
            <option value="">Elegir…</option>
            {organizations?.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </label>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          onChange={onFiles}
          className="hidden"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={!organizationId || upload.isPending}
          className="gap-2"
        >
          {upload.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Upload className="size-4" />
          )}
          Subir documentos
        </Button>

        {pending > 0 && (
          <Button
            variant="outline"
            className="gap-2"
            disabled={extract.isPending}
            onClick={() =>
              extract.mutate(
                { organizationId },
                {
                  onSuccess: () => toast.success("Documentos leídos"),
                  onError: () => toast.error("No se pudieron leer"),
                },
              )
            }
          >
            {extract.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            Leer {pending} pendiente(s) con IA
          </Button>
        )}

        {/* Reprocesar es para cuando cambia el prompt o el esquema: lo guardado
            se queda corto y hay que volver a leer todo, no solo lo pendiente. */}
        {organizationId && (documents?.length ?? 0) > 0 && (
          <Button
            variant="ghost"
            className="gap-2"
            disabled={extract.isPending}
            onClick={() => {
              if (
                !confirm(
                  `Vuelve a leer los ${documents!.length} documentos activos con IA. Toma varios minutos. ¿Continuar?`,
                )
              )
                return;
              extract.mutate(
                { organizationId, all: true },
                {
                  onSuccess: () => toast.success("Documentos reprocesados"),
                  onError: () => toast.error("No se pudieron reprocesar"),
                },
              );
            }}
          >
            {extract.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            Reprocesar todos
          </Button>
        )}

        <p className="text-xs text-muted-foreground">
          Hasta 15 MB por archivo. Se puede subir varios de una vez.
        </p>
      </div>

      {isLoading && <Skeleton className="h-32 rounded-xl" />}

      {organizationId && documents?.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          <FileText className="mx-auto mb-2 size-6 opacity-40" />
          Esta empresa todavía no tiene documentos cargados.
        </p>
      )}

      <div className="space-y-3">
        {documents?.map((document) => (
          <DocumentRow
            key={document.organizationdocument_id}
            document={document}
          />
        ))}
      </div>

      {documents && documents.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5" />
          Los documentos no se borran: uno desactivado sigue explicando los
          informes en los que se usó.
        </p>
      )}
    </div>
  );
}
